import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import SaveIcon from '@mui/icons-material/Save';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AppCard, { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import { withTranslation } from "react-i18next";
import { find, isEmpty, isEqual, isNull } from "lodash";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { RouteList } from "@plannel/routeList";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import companyService from "@plannel/services/data/company-service";
import debug from "@plannel/utils/debug-util";
import SelectExpTime from "@plannel/components/SelectExpTime";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";

const CompanyProfile = ({ t }) => {
  const userData = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [isLoading, setIsLoading] = useState(false);

  const [name, setName] = useState(null);
  const [coAddress, setCoAddress] = useState(null);
  const [telNum, setTelNum] = useState(null);
  const [email, setEmail] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState(null);
  const [ciImage, setCiImage] = useState();
  const [descTxt, setDescTxt] = useState(null);
  const [jwtExpireSec, setJwtExpireSec] = useState(43200);
  const [dashboardStartMonth, setDashboardStartMonth] = useState(null);
  const [logoChange, setLogoChange] = useState(false);
  const { snackBarMessageColor } = ColorUtil;
  const { getCurrencySymbol } = CodeUtil;

  const currencies = [
    { code: "us", label: "USD" },
    { code: "kr", label: "KRW" },
    { code: "jp", label: "JPY" },
    { code: "eu", label: "EUR" },
    { code: "cn", label: "CNY" },
    { code: "tw", label: "TWD" },
    { code: "vn", label: "VND" },
    { code: "id", label: "IDR" }
  ];
  const [baseCurrency, setBaseCurrency] = useState(null);

  const [previewURL, setPreviewURL] = useState("");
  const [preview, setPreview] = useState("");

  const fileRef = useRef(null);
  const allowedFileTypes = ["jpg", "png", "gif", "jpeg"];
  const fileMaxSize = 30720;

  const formatterDashboardDate = (month) => {
    const now = new ZDate();
    return new ZDate(now.getFullYear(), month - 1, 1, 1, 1);
  }

  const getCompanyInfo = () => {
    companyService.get()
      .then(
        (response) => {
          const { data } = response;
          if (data) {
            setName(data.name);
            setCoAddress(data.coAddress);
            setTelNum(data.telNum);
            setEmail(data.email);
            setWebsiteUrl(data.websiteUrl);
            setCiImage(data.ciImage);
            setDescTxt(data.descTxt);
            setJwtExpireSec(data.jwtExpireSec);
            if (data.customObj) {
              setDashboardStartMonth(formatterDashboardDate(data.customObj?.dbStartMon ?? 1));
            }
          }

          const currency = find(currencies, item => isEqual(item.label, data.currencyCd));
          if (currency) {
            setBaseCurrency(currency);
          } else {
            setBaseCurrency(currencies[0]);
          }

          if (data.ciImage.length > 0) {
            const imgSrc = `data:image/png;base64,${btoa(String.fromCharCode.apply(null, new Uint8Array(data.ciImage)))}`;
            setPreviewURL(imgSrc);
          }
        },
        (error) => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
          debug(error);
        })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const getSaveParameters = () => {
    const dbStartMon = dashboardStartMonth?.getMonth() + 1 ?? 1;
    const params = { name, coAddress, telNum, email, websiteUrl, ciImage, descTxt, jwtExpireSec, customObj: { dbStartMon } };
    params.currencyCd = isNull(baseCurrency) ? null : baseCurrency.label;

    return params;
  }

  const saveCompanyInfo = () => {
    if (isEmpty(name)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("companyName")}] ${t("MSG_FieldRequired")}` });
      return;
    }

    const params = getSaveParameters();
    companyService.update(params)
      .then(
        () => {
          if (ciImage) {
            const imgSrc = `data:image/png;base64,${btoa(String.fromCharCode.apply(null, new Uint8Array(ciImage)))}`;
            setPreviewURL(imgSrc);
          }

          if (baseCurrency) {
            userData.currencyCd = baseCurrency.label;
            userData.currencySymbol = getCurrencySymbol(baseCurrency.label)
            localStorage.setItem("user", JSON.stringify(userData));
          }
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          if (logoChange) {
            navigate(RouteList.Home.path, { state: { logoUpdate: true } });
          }
        },
        (error) => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          debug(error);
        }
      ).then(() => {
        setLogoChange(false);
      });
  }

  const handleFileOnChange = (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    const fileExtension = file.name.split(".").at(-1);
    const reader = new FileReader();
    const reader_file = new FileReader();

    if (!allowedFileTypes.includes(fileExtension)) {
      setDialogInfo({ open: true, title: "info", content: t("MSG_NotAllowFormat") })
      return false;
    }

    if (file.size > fileMaxSize) {
      setDialogInfo({ open: true, title: "info", content: t("MSG_MaxFileSize") })
      return false;
    }
    //file preview
    reader.onloadend = () => {
      setPreviewURL(reader.result);
    }
    if (file) {
      reader.readAsDataURL(file);
    }

    reader_file.readAsArrayBuffer(file);
    //file save 
    reader_file.onloadend = (e) => {
      const fileByteArray = [];
      const arrayBuffer = e.target.result, array = new Uint8Array(arrayBuffer);
      for (const element of array) {
        fileByteArray.push(element);
      }
      setCiImage(fileByteArray);
      setLogoChange(true);
    }

    return true;
  }

  const handleFileButtonClick = (e) => {
    e.preventDefault();
    fileRef.current.click();
  }

  const handleDeleteButtonClick = (e) => {
    e.preventDefault();
    setPreview("");
    setCiImage();
    setPreviewURL("");
    setLogoChange(true);
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveCompanyInfo();
    }
  };

  useEffect(() => {
    if (previewURL !== '')
      setPreview(<img style={{ height: '60px' }} className='img_preview' alt='preview' src={previewURL}></img>);
  }, [previewURL])

  useEffect(() => {
    try {
      setIsLoading(true);
      if (!isLoading) {
        getCompanyInfo();
      }
    } catch (error) {
      debug(error);
    }
    return () => {
      setIsLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box id="company-profile" sx={{ height: "100%", overflow: "auto", px: 3, py: 2 }}>
        <Typography sx={{ fontWeight: 700 }} variant="h5">{t("companyProfile")}</Typography>
        <Divider sx={{ borderColor: "primary.main", borderBottomWidth: 2, mt: 1, mb: 3 }} />

        <Box sx={{ maxWidth: 1160 }}>
          <Grid container spacing={2.5} alignItems="flex-start">
            {/* 우: 로고 / 브랜딩 */}
            <Grid item xs={12} md={4} sx={{ order: { xs: 2, md: 2 } }}>
              <AppCard>
                <CardContent sx={{ display: "flex", flexDirection: "column", p: 2.5 }}>
                  <Typography sx={{ ...CARD_HEADER_SX, mb: 2 }}>{t("logo")}</Typography>
                  <Box sx={{
                    minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center",
                    border: "1px dashed", borderColor: "divider", borderRadius: 1.5, bgcolor: "#FAFBFC", p: 2,
                  }}>
                    {preview ? preview : <ImageOutlinedIcon sx={{ fontSize: 44, color: "text.disabled" }} />}
                  </Box>
                  <input ref={fileRef} hidden id="file" type="file" accept=".png, .jpg, .jpeg, .gif" onChange={handleFileOnChange} />
                  <Box sx={{ mt: 2, display: "flex", gap: 1.5, justifyContent: "center" }}>
                    <Button size="small" color="primary" variant="contained" disableElevation component="span"
                      onClick={handleFileButtonClick}>LOGO {t("upload")}</Button>
                    <Button size="small" color="primary" variant="outlined" component="span"
                      onClick={handleDeleteButtonClick}>LOGO {t("delete")}</Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, textAlign: "center" }}>
                    JPG · PNG · GIF · Max 30KB
                  </Typography>
                </CardContent>
              </AppCard>
            </Grid>

            {/* 좌: 상세 */}
            <Grid item xs={12} md={8} sx={{ order: { xs: 1, md: 1 } }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* 회사 정보 */}
                <AppCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography sx={{ ...CARD_HEADER_SX, mb: 2 }}>{t("companyInformation")}</Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth required id="name" name="name" label={t("companyName")}
                          value={name ? name : ''} onChange={(e) => setName(e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={currencies}
                          getOptionLabel={option => option.label}
                          value={baseCurrency}
                          isOptionEqualToValue={(option, value) => option.code === value.code}
                          onChange={(_, newValue) => setBaseCurrency(newValue)}
                          disableClearable
                          renderOption={(props, option) => (
                            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                              <img loading="lazy" width="20" alt=""
                                src={`https://flagcdn.com/w20/${option.code}.png`}
                                srcSet={`https://flagcdn.com/w40/${option.code}.png 2x`}
                              />
                              {`${option.label}, ${getCurrencySymbol(option.label)}`}
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField {...params} fullWidth label={t("baseCurrency")} />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth multiline minRows={2} id="coAddress" name="coAddress" label={t("address")}
                          value={coAddress ? coAddress : ''} onChange={(e) => setCoAddress(e.target.value)} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth multiline minRows={2} id="descTxt" name="descTxt" label={t("descTxt")}
                          value={descTxt ? descTxt : ''} onChange={(e) => setDescTxt(e.target.value)} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </AppCard>

                {/* 연락처 */}
                <AppCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography sx={{ ...CARD_HEADER_SX, mb: 2 }}>{t("contact")}</Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth id="telNum" name="telNum" label={t("telNumber")}
                          value={telNum ? telNum : ''} onChange={(e) => setTelNum(e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth id="email" name="email" label={t("emailAddress")}
                          value={email ? email : ''} onChange={(e) => setEmail(e.target.value)} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth id="websiteUrl" name="websiteUrl" label={t("websiteUrl")}
                          value={websiteUrl ? websiteUrl : ''} onChange={(e) => setWebsiteUrl(e.target.value)} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </AppCard>

                {/* 환경설정 */}
                <AppCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography sx={{ ...CARD_HEADER_SX, mb: 2 }}>{t("preferences")}</Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label={t("fiscalStartMon")}
                            views={["month"]}
                            format={"MM"}
                            value={dashboardStartMonth}
                            openTo={"month"}
                            onAccept={setDashboardStartMonth}
                            slotProps={{ textField: { fullWidth: true, sx: { '& .MuiButtonBase-root': { mr: "0 !important" } } } }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SelectExpTime
                          loginExpTime={jwtExpireSec}
                          setLoginExpTime={setJwtExpireSec}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </AppCard>
              </Box>
            </Grid>
          </Grid>

          {/* 저장 */}
          <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 3, mb: 2 }}>
            <Button size="large" color="primary" variant="contained" disableElevation
              startIcon={<SaveIcon />}
              onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })}>{t("save")}</Button>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(CompanyProfile);